/**
 * Cookie consent
 * ==============
 *
 * Gates every non-essential cookie behind an explicit choice, and remembers
 * that choice in a first-party cookie.
 *
 * Design notes, because a few of these are deliberate and easy to "fix" wrongly:
 *
 * - FAIL CLOSED. The banner markup ships hidden and is only revealed once this
 *   module has run. If the bundle fails to load, the visitor sees no banner AND
 *   no analytics: the safe pairing. Never invert this by showing the banner in
 *   markup and hiding it with JS.
 *
 * - CLIENT-SIDE GATE, NOT SERVER-RENDERED. The choice is applied in the browser
 *   so that the HTML is byte-identical for every visitor. That keeps the page
 *   safe to full-page cache (Servd/Cloudflare); a server-rendered gate would
 *   leak one visitor's consent state to the next once static caching is on.
 *
 * - NO CONSENT LOG. We store the choice on the visitor's own device and nothing
 *   server-side. Evidence of compliance is the versioned banner copy in this
 *   repo, not a per-visitor record — building one would mean minting an
 *   identifier for someone who is otherwise anonymous.
 *
 * - NO INTERACTION MEANS NO CONSENT. Dismissing without choosing writes nothing,
 *   so the banner returns on the next visit. Do not add a close control that
 *   silently persists a "seen it" flag.
 *
 * Events, which are the integration contract for anything outside this module:
 *
 *   hj-consent:open    fire on window to reopen the preferences panel (the
 *                      footer "Cookie Settings" control does exactly this)
 *   hj-consent:change  fired on window after any decision, detail = the
 *                      granted categories. Third-party embeds should gate on
 *                      this rather than reading the cookie themselves.
 */

const DEFAULT_SETTINGS = {
    cookieName: 'hj_consent',
    version: 1,
    lifetimeDays: 182,
    googleAnalyticsId: null,
};

/** Categories the visitor can actually change. Strictly necessary is not here:
 *  it is not optional, so offering a toggle for it would be theatre. */
const OPTIONAL_CATEGORIES = ['analytics'];

/* ------------------------------------------------------------------ cookies */

function readCookie(name) {
    const match = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)')
    );

    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const secure = location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Remove the cookies Google Analytics sets, on every domain scope it might have
 * used. GA writes `_ga` on the registrable domain with a leading dot, so
 * clearing only the current host silently leaves it behind.
 */
function clearAnalyticsCookies() {
    const host = location.hostname;
    const parts = host.split('.');
    const scopes = [null, host];

    // Walk UP the label list rather than guessing the registrable domain.
    // "last two labels" is wrong under a multi-label public suffix: on
    // www.example.co.uk it produces `.co.uk` (a public suffix, rejected by the
    // browser) and never tries `.example.co.uk`, which is where GA actually
    // writes _ga — so withdrawal would leave the client id on the device.
    // Trying every suffix is safe: the browser silently ignores the ones it
    // will not accept.
    for (let i = 0; i < parts.length - 1; i += 1) {
        scopes.push(`.${parts.slice(i).join('.')}`);
    }

    document.cookie.split(';').forEach((pair) => {
        const name = pair.split('=')[0].trim();

        if (!/^_ga/.test(name)) {
            return;
        }

        scopes.forEach((scope) => {
            document.cookie = `${name}=; Max-Age=0; Path=/${scope ? `; Domain=${scope}` : ''}`;
        });
    });
}

/* ------------------------------------------------------------------ loaders */

/**
 * Inject gtag.js. Called only from a granted analytics state — there is
 * deliberately no other caller, and no code path that runs this on page load.
 */
function loadGoogleAnalytics(id, lifetimeDays) {
    if (!id) {
        return;
    }

    // Lift any standing opt-out BEFORE the already-injected check. Withdrawing
    // and then re-accepting within one page leaves gtag.js loaded but disabled;
    // if this sat after the early return, the second consent would be recorded
    // and the banner would close while GA stayed permanently switched off.
    window[`ga-disable-${id}`] = false;

    if (window.__hjAnalyticsInjected) {
        if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', { analytics_storage: 'granted' });
        }

        return;
    }

    window.__hjAnalyticsInjected = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', id, {
        // Do not let the analytics cookie outlive the consent that allowed it.
        // GA's own default is two years, which would outlast our re-ask window.
        cookie_expires: lifetimeDays * 86400,
    });
}

/**
 * Stop an already-running gtag.js without navigating.
 *
 * `ga-disable-<ID>` is Google's documented kill switch: once true, gtag sends
 * nothing further for that property. We deliberately do NOT reload the page to
 * achieve this. A reload discards whatever the visitor had typed into a form
 * (the contact and beta forms are both AJAX, so nothing repopulates them), and
 * on a page that was itself rendered from a POST it silently replays that POST
 * — which with synchronous Brevo integrations would duplicate a real
 * submission. Someone turning analytics off should not lose their enquiry.
 */
function disableGoogleAnalytics(id) {
    if (id) {
        window[`ga-disable-${id}`] = true;
    }

    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
}

/* -------------------------------------------------------------------- store */

function emptyChoices() {
    return OPTIONAL_CATEGORIES.reduce((acc, key) => ({ ...acc, [key]: false }), {});
}

/**
 * Read a stored decision. Returns null when there is nothing usable — no cookie,
 * unparseable, or recorded against an older banner version (in which case the
 * visitor agreed to wording they can no longer see, so we ask again).
 */
function readStoredConsent(settings) {
    const raw = readCookie(settings.cookieName);

    if (!raw) {
        return null;
    }

    let parsed;

    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return null;
    }

    if (!parsed || parsed.v !== settings.version) {
        return null;
    }

    return OPTIONAL_CATEGORIES.reduce(
        (acc, key) => ({ ...acc, [key]: parsed[key] === true }),
        {}
    );
}

function storeConsent(settings, choices) {
    const payload = {
        v: settings.version,
        ts: Math.floor(Date.now() / 1000),
        ...choices,
    };

    writeCookie(settings.cookieName, JSON.stringify(payload), settings.lifetimeDays);
}

/* ---------------------------------------------------------------- component */

/**
 * `parseInt(x, 10) || fallback` is not good enough here: it turns "2abc" into 2
 * and 0 into the fallback. The version is the one thing that decides whether a
 * stored choice is still valid, so a malformed value must fall back loudly to
 * the default rather than half-parse into something plausible.
 */
function wholeNumber(value, fallback) {
    const parsed = Number(value);

    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

function settingsFromElement(el) {
    const data = el.dataset;

    return {
        cookieName: data.consentCookie || DEFAULT_SETTINGS.cookieName,
        version: wholeNumber(data.consentVersion, DEFAULT_SETTINGS.version),
        lifetimeDays: wholeNumber(data.consentDays, DEFAULT_SETTINGS.lifetimeDays),
        googleAnalyticsId: data.consentGaId || null,
    };
}

const Consent = {
    /**
     * Alpine component. Registered as `cookieConsent` in app.js, before
     * Alpine.start() — Alpine.data() is ignored after start.
     */
    component() {
        return {
            settings: DEFAULT_SETTINGS,

            /**
             * `granted` is what is actually in force. `choices` is the working
             * copy the panel's toggles are bound to.
             *
             * These MUST stay separate. x-model mutates `choices` the moment a
             * toggle is clicked, so anything that needs to know the previous
             * state (such as "were they using analytics before this change?")
             * has to read `granted`. Collapsing these two back into one field
             * silently breaks withdrawal: commit() reads the value the toggle
             * has already changed, concludes nothing was withdrawn, and leaves
             * gtag.js running on the page.
             *
             * It also means closing the panel without saving discards the
             * working copy rather than leaving the toggles out of step with
             * what was actually stored.
             */
            granted: emptyChoices(),
            choices: emptyChoices(),

            decided: false,
            bannerOpen: false,
            panelOpen: false,
            returnFocusTo: null,
            previousBodyOverflow: '',
            previousBodyPadding: '',

            init() {
                this.settings = settingsFromElement(this.$el);

                const stored = readStoredConsent(this.settings);

                if (stored) {
                    this.granted = stored;
                    this.choices = { ...stored };
                    this.decided = true;
                    this.apply(stored);
                } else {
                    // No usable decision — which includes a visitor whose
                    // previous choice was invalidated by a version bump. They
                    // may still be carrying a GA client id from that earlier
                    // consent, so clear it rather than letting it survive the
                    // re-ask and stitch the sessions back together.
                    clearAnalyticsCookies();
                    this.bannerOpen = true;
                }

                // Anything that wants to reopen the panel talks to us through
                // this event.
                window.addEventListener('hj-consent:open', () => this.openPanel());

                // The footer control is bound here rather than with an inline
                // onclick, so the markup carries no executable attribute for a
                // Content-Security-Policy to block. Delegated, so it does not
                // depend on this component initialising before the footer.
                document.addEventListener('click', (event) => {
                    if (event.target.closest('[data-cookie-settings]')) {
                        event.preventDefault();
                        this.openPanel();
                    }
                });

                // Keep the fixed bar from sitting on top of page content while
                // the visitor has not decided. Without this it can permanently
                // cover the bottom of a short page — and on mobile the bar
                // stacks to a tall column, so that can be a form's submit
                // button.
                this.previousBodyPadding = document.body.style.paddingBottom;

                if (window.ResizeObserver && this.$refs.bar) {
                    new ResizeObserver(() => this.syncBannerInset()).observe(this.$refs.bar);
                }

                this.$nextTick(() => this.syncBannerInset());
            },

            /* -------------------------------------------------------- actions */

            acceptAll() {
                this.commit(
                    OPTIONAL_CATEGORIES.reduce((acc, key) => ({ ...acc, [key]: true }), {})
                );
            },

            rejectAll() {
                this.commit(emptyChoices());
            },

            savePreferences() {
                this.commit({ ...this.choices });
            },

            commit(choices) {
                storeConsent(this.settings, choices);

                this.granted = { ...choices };
                this.choices = { ...choices };
                this.decided = true;
                this.bannerOpen = false;
                this.$nextTick(() => this.syncBannerInset());

                // releasePanel(), NOT `panelOpen = false`. Saving from inside
                // the panel is the normal path in from the footer control, and
                // setting the flag directly left document.body scroll-locked
                // for the rest of the visit.
                this.releasePanel();

                this.apply(choices);
            },

            /**
             * Act on a decision. Granting loads what was granted; withdrawing
             * stops gtag in place and clears what it set.
             *
             * This deliberately does not reload the page — see
             * disableGoogleAnalytics(). The event is dispatched on EVERY path,
             * including withdrawal, because teardown is exactly when a consumer
             * gating an embed most needs to hear about it.
             */
            apply(choices) {
                if (choices.analytics) {
                    loadGoogleAnalytics(this.settings.googleAnalyticsId, this.settings.lifetimeDays);
                } else {
                    disableGoogleAnalytics(this.settings.googleAnalyticsId);
                    clearAnalyticsCookies();
                }

                window.dispatchEvent(
                    new CustomEvent('hj-consent:change', { detail: { ...choices } })
                );
            },

            /* ---------------------------------------------------------- panel */

            /**
             * Focus is moved explicitly rather than left to x-trap. x-trap
             * evaluates while the panel's parent is still `display: none`, so
             * its own initial focus() is a no-op and the dialog opens with
             * focus stranded on the page behind it. x-trap still does the
             * trapping; this just gives it somewhere to start.
             */
            openPanel() {
                // Re-entrancy guard. hj-consent:open is a documented public
                // event, so it can be fired twice or fired while the panel is
                // already open; without this the second call would capture
                // 'hidden' as the value to "restore" and lock the page for good.
                if (this.panelOpen) {
                    return;
                }

                // Start from what is actually in force, so a previous
                // open-toggle-cancel cycle cannot leave stale toggle positions.
                this.choices = { ...this.granted };
                this.returnFocusTo = document.activeElement;
                this.panelOpen = true;

                // Restore rather than blank on close: the mobile nav locks body
                // scroll too, and blanking would silently unlock it.
                this.previousBodyOverflow = document.body.style.overflow;
                document.body.style.overflow = 'hidden';

                this.$nextTick(() => {
                    if (this.$refs.dialog) {
                        this.$refs.dialog.focus();
                    }
                });
            },

            /** ESC and the close control both land here. Closing is NOT a
             *  decision: nothing is written, so an undecided visitor still sees
             *  the banner and still has no non-essential cookies. */
            /**
             * Give the panel's scroll lock back. Safe to call when the panel is
             * not open, which matters because commit() can be reached straight
             * from the banner.
             */
            /** Reserve exactly as much space as the bar currently occupies. */
            syncBannerInset() {
                const bar = this.$refs.bar;

                document.body.style.paddingBottom =
                    this.bannerOpen && bar && bar.offsetHeight
                        ? `${bar.offsetHeight}px`
                        : this.previousBodyPadding;
            },

            releasePanel() {
                if (!this.panelOpen) {
                    return;
                }

                this.panelOpen = false;
                document.body.style.overflow = this.previousBodyOverflow || '';
                this.previousBodyOverflow = '';
            },

            closePanel() {
                // Alpine binds @keydown.escape.window at init and x-show does
                // not gate it, so this runs on EVERY Escape press anywhere on
                // the site. Without this guard each one would blank
                // document.body.style.overflow and release a scroll lock set by
                // some entirely unrelated component.
                if (!this.panelOpen) {
                    return;
                }

                this.releasePanel();
                this.bannerOpen = !this.decided;
                this.$nextTick(() => this.syncBannerInset());

                // Send focus back where it came from, so closing with ESC does
                // not dump a keyboard user at the top of the document.
                const trigger = this.returnFocusTo;
                this.returnFocusTo = null;

                if (trigger && typeof trigger.focus === 'function') {
                    this.$nextTick(() => trigger.focus());
                }
            },
        };
    },
};

export default Consent;
